// src/pages/PublicMicrositePage.tsx

import { useEffect, useState } from 'react';
import { getPublicMicrosite, logMicrositeEvent, submitMicrositeEmailSignup } from '@/api/micrositePublic';
import type { PublicMicrositeData } from '@/types/microsite';
import MicrositeRenderer from '@/components/kit/microsite/MicrositeRenderer';
import MicrositeReportModal from '@/components/kit/microsite/MicrositeReportModal';

interface PublicMicrositePageProps {
  slug: string;
}

export default function PublicMicrositePage({ slug }: PublicMicrositePageProps) {
  const [pageData, setPageData] = useState<PublicMicrositeData | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!slug) return;

    // Extract tracking ID from query string
    const params = new URLSearchParams(window.location.search);
    const trackingId = params.get('id') || undefined;

    const fetchPage = async () => {
      try {
        const data = await getPublicMicrosite(slug);
        setPageData(data);

        // Log page view
        logMicrositeEvent(slug, {
          event_type: 'page_view',
          referrer: document.referrer || undefined,
          tracking_id: trackingId,
        }).catch(() => {}); // Fire and forget
      } catch (err: any) {
        setError(err?.error || { code: 'NOT_FOUND', message: 'Page not found' });
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  // Set the browser tab title
  useEffect(() => {
    if (pageData?.artist_name) {
      document.title = pageData.artist_name;
    } else if (error) {
      document.title = error.code === 'SUSPENDED' ? 'Page Not Available' : 'Page Not Found';
    }
  }, [pageData, error]);

  const handleLinkClick = (linkId: string, label: string) => {
    if (!slug) return;
    logMicrositeEvent(slug, {
      event_type: 'link_click',
      link_id: linkId,
      link_label: label,
    }).catch(() => {});
  };

  const handleEmailSubmit = async (email: string) => {
    if (!slug) return;
    await submitMicrositeEmailSignup(slug, email);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-white" />
      </div>
    );
  }

  // Error states
  if (error) {
    if (error.code === 'SUSPENDED') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
          <svg className="mb-4 h-16 w-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h1 className="text-xl font-bold text-white">Page Not Available</h1>
          <p className="mt-2 text-sm text-gray-400">This page has been removed for violating our terms of service.</p>
          <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">Powered by BandJerry</a>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
        <svg className="mb-4 h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
        <h1 className="text-xl font-bold text-white">Page Not Found</h1>
        <p className="mt-2 text-sm text-gray-400">This page doesn't exist or isn't available right now.</p>
        <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">Create your own — Powered by BandJerry</a>
      </div>
    );
  }

  if (!pageData) return null;

  return (
    <div className="min-h-screen">
      <MicrositeRenderer
        data={pageData}
        onLinkClick={handleLinkClick}
        onEmailSubmit={handleEmailSubmit}
      />

      {/* Report button - fixed at bottom */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={() => setShowReport(true)}
          className="rounded-full bg-black/30 px-3 py-1.5 text-xs text-white/50 backdrop-blur-sm transition-colors hover:text-white/80"
        >
          Report
        </button>
      </div>

      {showReport && slug && (
        <MicrositeReportModal slug={slug} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
